import { useRef } from 'react';
import type { JSX } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { FormattedMessage } from 'react-intl';
import { FieldArray, Field } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import Heading from '../../../common/Heading';
import formStyles from '../../../styles/forms.module.scss';
import s from './StepWindFacilityData.module.scss';
import WindFacilityForm from '../FacilityForm/WindFacilityForm';
import AddFacilityButton from '../FacilityButtons/AddFacilityButton';
import FileUploadInput from '../../../common/FormFields/FileUploadInput';
import { ALLOWED_FILE_EXTENSIONS_WIND } from '../../../common/FormFields/shared/fileUpload';
import { AccessibleDatepickerInput } from '../../../common/FormFields';
import type {
  FacilityWithId,
  StepFacilityDataProps,
} from '../FacilityForm/facilityFormTypes';
import { createDeleteFacilityHandler } from './facilityStepUtils';
import FacilityFormTransition from './FacilityFormTransition';

function StepWindFacilityData({ values, form }: StepFacilityDataProps) {
  const addFacilityButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <section className={s.stepWindFacilityData}>
      <Heading level={2} className={`${s.heading} ${s.desktopHeading}`}>
        <FormattedMessage id="offerWizard.StepWindFacilityData.title.desktop" />
      </Heading>
      <Heading level={2} className={`${s.heading} ${s.mobileHeading}`}>
        <FormattedMessage id="offerWizard.StepWindFacilityData.title.mobile" />
      </Heading>
      <div
        className={`${formStyles.formFields} ${s.preventScrollOnNewFacility}`}
      >
        <div className={formStyles.inputPair}>
          <Field
            name="information.start"
            labelMsg="offerWizard.StepSomeInformation.start.label.directmarketing"
            component={AccessibleDatepickerInput}
            minDate={new Date()}
          />
          <Field
            name="information.end"
            labelMsg="offerWizard.StepSomeInformation.end.label.directmarketing"
            component={AccessibleDatepickerInput}
            minDate={() => {
              if (values.information?.start) {
                return new Date(values.information.start);
              }
              return new Date();
            }}
          />
        </div>
        <hr className={formStyles.separator} />
        <FieldArray
          name="windFacilityData"
          render={(arrayHelpers) => {
            let facilityForms: JSX.Element[] | undefined;
            const deleteFacility = createDeleteFacilityHandler(
              'windFacilityData',
              values,
              arrayHelpers,
              addFacilityButtonRef
            );

            if (values.windFacilityData?.length) {
              facilityForms = values.windFacilityData.map(
                (data: FacilityWithId, index) => (
                  <FacilityFormTransition key={data.id}>
                    <WindFacilityForm
                      id={data.id}
                      index={index}
                      arrayHelpers={arrayHelpers}
                      values={values}
                      form={form}
                      deleteFacility={deleteFacility}
                    />
                  </FacilityFormTransition>
                )
              );
            }
            return (
              <>
                <TransitionGroup>{facilityForms}</TransitionGroup>
                <AddFacilityButton
                  ref={addFacilityButtonRef}
                  onClick={() => arrayHelpers.push({ id: uuidv4() })}
                />
              </>
            );
          }}
        />
      </div>
      <div className={s.facilityDetails} id="facilityDetailsDescription">
        <legend className={s.legend}>
          <FormattedMessage id="offerWizard.StepWindFacilityData.facilityDetails" />
        </legend>
        <div>
          <FormattedMessage id="offerWizard.StepWindFacilityData.facilityDetails.text" />
        </div>
      </div>
      <div className={formStyles.inputPairSingle}>
        <Field
          name="windFacilityFile"
          component={FileUploadInput}
          allowedExtensions={ALLOWED_FILE_EXTENSIONS_WIND}
          ariaDescribedBy="facilityDetailsDescription"
        />
      </div>
    </section>
  );
}

export default StepWindFacilityData;
